import { afterNextRender, Component, ElementRef, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { UtilsService } from 'core/utils/utils.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AccountsDetailService } from '../services/accounts-detail.service';
import { AccountNote } from '../../dashboard/entities/accountNote';
import { ACCOUNT_DASHBOARD_SECTIONS, TOUCHPOINT_NAVIGATION_PARAMS } from '../../touchpoints/entities/touchpoints-entity';

@Component({
  selector: 'app-accounts-detail',
  templateUrl: './accounts-detail.component.html',
  styleUrls: ['./accounts-detail.component.scss'],
  standalone: false
})
export class AccountsDetailComponent implements OnInit, OnDestroy {
  fragmentMap = ['', 'b2b-users', 'contacts', 'touchpoints', 'history', 'invoices', 'ln-orders'];
  customerId;
  leadId;
  tab: string;
  entityId;
  // sub pages select what components should be handled within the tab
  subPage = null;
  selectedTab;
  appStrings;
  customerDetails;
  leadDetails;
  isEditable = false;
  bpNumber;
  accountName1;
  accountName2;
  customerCountryCode;
  subs = new Subscription();
  canEdit: boolean;
  accountIsLoaded = false;
  isOnline: boolean;
  accountId;
  toggleValues = [];
  breadcrumbs = [];
  isMobile: boolean;
  isAdmin = this.appInfoService.isAdminTypeUser();
  canOpenTouchpoints = false;
  @ViewChild('notesWidget', { read: ElementRef }) notesWidget: ElementRef<HTMLElement>;
  private accountEmployeeId: string | null = null;
  private scrollToNotes = false;
  private notesLoaded = false;
  private quotesLoaded = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private appInfoService: AppInfoService,
    private appStateService: AppStateService,
    private accountDetailsService: AccountsDetailService,
    private deviceService: DeviceDetectorService,
    private utilsService: UtilsService,
    private injector: Injector
  ) { }

  ngOnInit() {
    this.isOnline = this.appStateService.state.isOnline;
    this.isMobile = this.deviceService.isMobile();
    this.customerId = this.route.snapshot.paramMap.get('customerId');
    this.leadId = this.route.snapshot.paramMap.get('leadId');
    this.appStrings = this.appInfoService.appStrings;
    this.breadcrumbs = [
      {
        url: '/accounts',
        icon: this.isMobile ? 'arrow_back' : null
      },
      {
        label: this.appStrings.contacts_accountDetails,
      },
    ];
    this.setToggleValues();

    if (this.customerId) {
      this.accountDetailsService.getAccountDetailsByCustomer(this.customerId).subscribe(result => this.setValues(result));
    } else {
      if (this.leadId) {
        this.accountDetailsService.getAccountDetailsByLead(this.leadId).subscribe(result => this.setValues(result, true));
      }
    }

    this.checkParams();
    this.subs.add(this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.checkParams();
      }
    }));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  updateTab(tabIndex: number) {
    if (this.selectedTab === tabIndex) {
      return;
    }
    this.selectedTab = tabIndex;
    const pathArray = [
      '/' + this.route.snapshot.url[0].path,
      this.customerId || this.leadId
    ];

    if (this.fragmentMap[tabIndex]) {
      pathArray.push({ tab: this.fragmentMap[tabIndex] });
    }
    this.router.navigate(pathArray, { queryParams: { [TOUCHPOINT_NAVIGATION_PARAMS.section]: null }, queryParamsHandling: 'merge' });
    // triger a resize event to force mat-tab-ink (red bar) to resize properly
    setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
  }

  editLead() {
    this.router.navigate(['leads/lead/edit', this.leadId]);
  }

  goBack() {
    this.router.navigate(['/accounts']);
  }

  onNewContactClick() {
    const baseAccountsRoute = '/' + this.route.snapshot.url[0].path;
    this.router.navigate([baseAccountsRoute, this.customerId || this.leadId, 'contacts', 'new']);
  }

  openTouchpointFromNote(note: AccountNote) {
    const { openTouchpointId, calendarDate, employeeId } = TOUCHPOINT_NAVIGATION_PARAMS;
    this.router.navigate(['/touchpoints'], {
      queryParams: {
        [openTouchpointId]: note.touchpointId,
        [calendarDate]: note.touchpointStartDate ? this.utilsService.toLocalDateString(new Date(note.touchpointStartDate)) : null,
        [employeeId]: this.isAdmin ? this.accountEmployeeId : null
      }
    });
  }

  onNotesLoaded() {
    this.notesLoaded = true;
    this.scrollToNotesWhenReady();
  }

  onQuotesLoaded() {
    this.quotesLoaded = true;
    this.scrollToNotesWhenReady();
  }

  /** Waits for the quotes widget too, because it changes the page height after it loads. */
  private scrollToNotesWhenReady() {
    if (!this.scrollToNotes || !this.notesLoaded || !this.quotesLoaded) {
      return;
    }
    this.scrollToNotes = false;
    afterNextRender(() => this.notesWidget?.nativeElement.scrollIntoView({ block: 'start' }), { injector: this.injector });
  }

  /** Runs on every navigation, because Last Visited from the Touchpoints tab reuses this component. */
  private checkTouchpointNavigationParams() {
    this.scrollToNotes = this.route.snapshot.queryParamMap.get(TOUCHPOINT_NAVIGATION_PARAMS.section) === ACCOUNT_DASHBOARD_SECTIONS.notes;
    if (this.scrollToNotes) {
      // The Dashboard tab widgets are recreated, so wait for their fresh load
      this.notesLoaded = false;
      this.quotesLoaded = false;
    }
  }

  private checkParams() {
    this.entityId = this.route.snapshot.paramMap.get('entityId');
    this.tab = this.route.snapshot.paramMap.get('tab');
    this.selectedTab = this.tab ? this.fragmentMap.indexOf(this.tab) : this.fragmentMap.indexOf('');
    this.checkTouchpointNavigationParams();

    if (this.entityId) {
      if (this.tab === 'contacts') {
        const baseRoute = this.customerId ? '/accounts' : '/accounts-leads';
        const accountId = this.customerId || this.leadId;
        this.router.navigate([baseRoute, accountId, 'contacts', this.entityId]);
        return;
      }
      this.subPage = 'editTouchpoint';
    } else {
      this.subPage = '';
    }
  }

  get selectedToggleIndex(): number {
    return this.toggleValues.findIndex(t => t.index === this.selectedTab);
  }

  private setToggleValues() {
    this.toggleValues = [
      { value: this.appStrings.menu_Dashboard, index: 0 },
      ...(!this.leadId ? [{ value: this.appStrings.menu_B2BUsers, index: 1 }] : []),
      { value: this.appStrings.menu_CustomerContactsTab, index: 2 },
      { value: this.appStrings.menu_CustomerTouchpointsTab, index: 3 },
      ...(!this.leadId ? [
        { value: this.appStrings.menu_transactionHistory, index: 4 },
        { value: this.appStrings.menu_Invoices, index: 5 },
        { value: this.appStrings.menu_lnOrders, index: 6 }
      ] : [])
    ];
  }

  private setValues(values: any, isLead?: boolean) {
    values.priceMatrix = values.priceMatrix || '';
    values.paymentTerms = values.paymentTerms || '';
    values.creditLimit = values.creditLimit || '';
    values.billToAddress = values.billToAddress || '';
    values.shipToAddress = values.shipToAddress || '';
    this.accountName1 = values.name1 || '';
    this.accountName2 = values.name2 || '';
    this.bpNumber = values.businessPartnerNo;
    this.canEdit = values.canEdit;
    this.accountId = values.id;
    this.customerCountryCode = values.countryId;
    this.accountEmployeeId = values.employeeId;
    // BE canEdit covers the account manager's employee id mappings; it's true for every non-AM role, hence the role check
    this.canOpenTouchpoints = this.isAdmin || (this.appInfoService.isAccountManager() && values.canEdit === true);

    if (isLead) {
      this.isEditable = values.leadStatus === 1;
      this.leadDetails = values;
    } else {
      this.customerDetails = values;
    }
    this.accountIsLoaded = true;
  }
}
